import {
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  Group,
  Button,
  Text,
} from "@mantine/core";
import {
  IconMail,
  IconClock,
  IconToggleRight,
  IconFileText,
  IconUsers,
  IconSettings,
  IconEye,
  IconTemplate,
  IconFileInvoice,
  IconFiles,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useMemo, useRef, useState } from "react";
import type {
  TriggerType,
  ConditionBalance,
  TemplateAudienceType,
  InvoiceInclusionScope,
} from "../api/queries/emailTemplateQueries";
import { useEmailTemplateVariablesQuery } from "../api/queries/emailTemplateQueries";
import { useCustomerGroupsQuery } from "../api/queries/customerGroupsQueries";
import { EmailTemplateVariablePicker } from "./EmailTemplateVariablePicker";
import {
  buildPreviewSubject,
  DEFAULT_INVOICE_LIST_FORMAT,
  hasSingularVarsInMultiPreview,
  renderInvoiceListPreview,
  renderTemplatePreview,
  type PreviewMode,
} from "../utils/emailTemplatePreview";
import {
  bodyVariablePickerMeta,
  subjectVariablePickerMeta,
  variablesForBodyPicker,
  variablesForListFormatPicker,
  variablesForSubjectPicker,
} from "../utils/emailTemplateVariables";

const triggerTypeOptions = [
  { value: "INVOICE_DATE", label: "Invoice Date" },
  { value: "DUE_DATE", label: "Due Date" },
  { value: "OVERDUE_RECURRING", label: "Overdue (recurring)" },
];

const inclusionScopeOptions = [
  { value: "ALL_OPEN", label: "All open invoices" },
  { value: "OVERDUE_OPEN", label: "Overdue open invoices" },
  { value: "OVERDUE_SINCE", label: "Overdue since minimum days" },
];

const conditionBalanceOptions = [
  { value: "ANY", label: "Any Balance" },
  { value: "GT_0", label: "Greater Than 0" },
];

const audienceTypeOptions = [
  { value: "SINGLE_INVOICE", label: "Single invoice" },
  { value: "MULTIPLE_INVOICES", label: "Multiple invoices" },
];

/** Hebrew Unicode ranges (letters, punctuation, presentation forms). */
function containsHebrew(text: string): boolean {
  if (!text) return false;
  return /[\u0590-\u05FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
}

function rtlInputStyles(isRtl: boolean) {
  return isRtl
    ? {
        direction: "rtl" as const,
        textAlign: "right" as const,
        unicodeBidi: "plaintext" as const,
      }
    : {
        direction: "ltr" as const,
        textAlign: "left" as const,
      };
}

function wrapPreviewHtml(html: string, isRtl: boolean): string {
  if (!html || !isRtl) return html;
  if (/<[^>]+dir\s*=\s*["']rtl/i.test(html)) return html;
  return `<div dir="rtl" style="direction: rtl; text-align: right;">${html}</div>`;
}

const inputLabelStyles = { label: { fontWeight: 500, marginBottom: 8 } };

function FormSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ size?: number; className?: string; stroke?: number }>;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-start gap-3 border-b border-gray-100 bg-[#FAFAFA] px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF4F2] text-[#4B7F73]">
          <Icon size={16} stroke={1.75} />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

const audienceTypeMeta: Record<
  TemplateAudienceType,
  { label: string; description: string; icon: typeof IconFileInvoice }
> = {
  SINGLE_INVOICE: {
    label: "Single invoice",
    description:
      "One email per invoice. Use {invoice_number}, {due_date}, and other per-invoice variables.",
    icon: IconFileInvoice,
  },
  MULTIPLE_INVOICES: {
    label: "Multiple invoices",
    description:
      "One email per customer. Put {invoice_list} in the body; use the list format for each invoice row.",
    icon: IconFiles,
  },
};

export interface TemplateFormData {
  name: string;
  subject: string;
  body: string;
  trigger_type: TriggerType;
  offset_days: number;
  condition_balance: ConditionBalance;
  audience_type: TemplateAudienceType;
  invoice_list_format: string;
  enabled: boolean;
  group_ids: number[];
  repeat_interval_days: number;
  invoice_inclusion_scope: InvoiceInclusionScope;
  inclusion_min_days_overdue: number;
}

type ActiveField = "subject" | "body" | "invoice_list_format";

interface EmailTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  formData: TemplateFormData;
  onFormDataChange: (data: TemplateFormData) => void;
  onSubmit: () => void;
  isEditing: boolean;
  isLoading: boolean;
}

function insertAtCursor(
  current: string,
  insertion: string,
  selectionStart: number,
  selectionEnd: number,
): { next: string; cursor: number } {
  const next =
    current.slice(0, selectionStart) + insertion + current.slice(selectionEnd);
  const cursor = selectionStart + insertion.length;
  return { next, cursor };
}

export function EmailTemplateModal({
  opened,
  onClose,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isLoading,
}: EmailTemplateModalProps) {
  const [activeField, setActiveField] = useState<ActiveField>("body");
  const [previewMode, setPreviewMode] = useState<PreviewMode>("single");
  const [textDirection, setTextDirection] = useState<"auto" | "rtl" | "ltr">(
    "auto",
  );
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const listFormatRef = useRef<HTMLTextAreaElement>(null);
  const { data: variables = [] } = useEmailTemplateVariablesQuery({
    enabled: opened,
  });
  const { data: customerGroups = [] } = useCustomerGroupsQuery({
    enabled: opened,
  });
  const customerGroupOptions = useMemo(
    () =>
      customerGroups.map((group) => ({
        value: String(group.id),
        label: group.name,
      })),
    [customerGroups],
  );

  const updateField = <K extends keyof TemplateFormData>(
    field: K,
    value: TemplateFormData[K],
  ) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const detectedRtl =
    containsHebrew(formData.name) ||
    containsHebrew(formData.subject) ||
    containsHebrew(formData.body);
  const isRtl =
    textDirection === "rtl" || (textDirection === "auto" && detectedRtl);

  const isSingleAudience = formData.audience_type === "SINGLE_INVOICE";
  const isRecurring = formData.trigger_type === "OVERDUE_RECURRING";
  const effectivePreviewMode: PreviewMode = isSingleAudience
    ? "single"
    : previewMode;

  const previewSubject = useMemo(
    () =>
      buildPreviewSubject(
        formData.subject,
        effectivePreviewMode,
        variables,
        isRtl,
      ),
    [formData.subject, effectivePreviewMode, variables, isRtl],
  );

  const previewBody = useMemo(() => {
    const rendered =
      renderTemplatePreview(
        formData.body,
        effectivePreviewMode,
        variables,
        isRtl,
        formData.invoice_list_format,
      ) || (isRtl ? "<i>לא הוזן תוכן</i>" : "<i>No content provided</i>");
    return wrapPreviewHtml(rendered, isRtl);
  }, [
    formData.body,
    formData.invoice_list_format,
    effectivePreviewMode,
    variables,
    isRtl,
  ]);

  const previewListFormat = useMemo(
    () =>
      renderInvoiceListPreview(
        formData.invoice_list_format,
        effectivePreviewMode,
        variables,
        isRtl,
      ),
    [formData.invoice_list_format, effectivePreviewMode, variables, isRtl],
  );

  const showMultiWarning = useMemo(
    () =>
      !isSingleAudience &&
      (hasSingularVarsInMultiPreview(formData.body, effectivePreviewMode) ||
        hasSingularVarsInMultiPreview(formData.subject, effectivePreviewMode)),
    [formData.body, formData.subject, effectivePreviewMode, isSingleAudience],
  );

  const subjectPickerVariables = useMemo(
    () => variablesForSubjectPicker(variables, formData.audience_type),
    [variables, formData.audience_type],
  );
  const bodyPickerVariables = useMemo(
    () => variablesForBodyPicker(variables, formData.audience_type),
    [variables, formData.audience_type],
  );
  const listFormatPickerVariables = useMemo(
    () => variablesForListFormatPicker(variables),
    [variables],
  );
  const subjectPickerMeta = subjectVariablePickerMeta(formData.audience_type);
  const bodyPickerMeta = bodyVariablePickerMeta(formData.audience_type);

  const handleInsertVariable = (token: string) => {
    const target =
      activeField === "subject"
        ? subjectRef.current
        : activeField === "invoice_list_format"
          ? listFormatRef.current
          : bodyRef.current;
    const currentValue =
      activeField === "subject"
        ? formData.subject
        : activeField === "invoice_list_format"
          ? formData.invoice_list_format
          : formData.body;

    if (!target) {
      if (activeField === "invoice_list_format") {
        updateField("invoice_list_format", `${currentValue}${token}`);
      } else {
        updateField(activeField, `${currentValue}${token}`);
      }
      return;
    }

    const start = target.selectionStart ?? currentValue.length;
    const end = target.selectionEnd ?? currentValue.length;
    const { next, cursor } = insertAtCursor(currentValue, token, start, end);
    if (activeField === "invoice_list_format") {
      updateField("invoice_list_format", next);
    } else {
      updateField(activeField, next);
    }

    requestAnimationFrame(() => {
      target.focus();
      target.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EEF4F2] text-[#4B7F73]">
            <IconTemplate size={20} stroke={1.75} />
          </div>
          <div className="min-w-0">
            <Text size="lg" fw={600} lh={1.3}>
              {isEditing ? "Edit Template" : "Create Template"}
            </Text>
            <Text size="sm" c="dimmed" mt={2}>
              {isEditing
                ? "Update content, triggers, and delivery settings."
                : "Set up automated payment reminder emails for your customers."}
            </Text>
          </div>
        </div>
      }
      size="lg"
      radius="md"
      padding="xl"
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          maxHeight: "min(90vh, 52rem)",
          display: "flex",
          flexDirection: "column",
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          paddingTop: 12,
        },
        header: {
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "16px",
          marginBottom: 0,
        },
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200">
          <FormSection
            icon={IconTemplate}
            title="Basics"
            description="Name, audience, and which customers receive this template."
          >
            <TextInput
              label="Template Name"
              placeholder={
                isRtl
                  ? "לדוגמה: תזכורת 3 ימים לפני מועד התשלום"
                  : "e.g., Reminder 3 days before due date"
              }
              value={formData.name}
              onChange={(e) => updateField("name", e.currentTarget.value)}
              required
              radius="md"
              dir={isRtl ? "rtl" : "ltr"}
              styles={{
                ...inputLabelStyles,
                input: rtlInputStyles(isRtl),
              }}
            />

            <MultiSelect
              label="Customer groups"
              placeholder="Default (all customers without a dedicated group)"
              data={customerGroupOptions}
              value={formData.group_ids.map(String)}
              onChange={(values) =>
                updateField(
                  "group_ids",
                  values.map((value) => Number(value)),
                )
              }
              searchable
              clearable
              radius="md"
              styles={inputLabelStyles}
            />

            <div>
              <Text size="sm" fw={500} mb={8}>
                Template type <span className="text-red-500">*</span>
              </Text>
              <div className="grid gap-3 sm:grid-cols-2">
                {audienceTypeOptions.map((option) => {
                  const meta = audienceTypeMeta[option.value as TemplateAudienceType];
                  const OptionIcon = meta.icon;
                  const selected = formData.audience_type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        const audienceType = option.value as TemplateAudienceType;
                        onFormDataChange({
                          ...formData,
                          audience_type: audienceType,
                          invoice_list_format:
                            formData.invoice_list_format ||
                            DEFAULT_INVOICE_LIST_FORMAT,
                        });
                      }}
                      className={`rounded-xl border p-3 text-left transition-colors ${
                        selected
                          ? "border-[#4B7F73] bg-[#EEF4F2] ring-1 ring-[#4B7F73]/20"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            selected
                              ? "bg-[#4B7F73] text-white"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <OptionIcon size={16} stroke={1.75} />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`text-sm font-medium ${
                              selected ? "text-[#3F6C61]" : "text-gray-900"
                            }`}
                          >
                            {meta.label}
                          </p>
                          <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                            {meta.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </FormSection>

          <FormSection
            icon={IconMail}
            title="Email content"
            description="Subject, body, and variables. HTML is supported."
          >
            <TextInput
              ref={subjectRef}
              label={
                <span
                  className={`inline-flex items-center gap-1.5 ${isRtl ? "flex-row-reverse" : ""}`}
                >
                  <IconMail size={16} className="shrink-0 text-gray-500" />
                  <span>Email Subject</span>
                </span>
              }
              placeholder={
                isRtl
                  ? "תזכורת תשלום — {company}"
                  : "Payment reminder — {company}"
              }
              value={formData.subject}
              onFocus={() => setActiveField("subject")}
              onChange={(e) => updateField("subject", e.currentTarget.value)}
              required
              radius="md"
              dir={isRtl ? "rtl" : "ltr"}
              styles={{
                ...inputLabelStyles,
                input: rtlInputStyles(isRtl),
              }}
            />

            <EmailTemplateVariablePicker
              variables={subjectPickerVariables}
              onInsert={handleInsertVariable}
              disabled={isLoading}
              label={subjectPickerMeta.label}
              description={subjectPickerMeta.description}
            />

            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-medium text-gray-700">
                Email Body <span className="text-red-500">*</span>
              </label>
              <div
                className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5"
                role="group"
                aria-label="Text direction"
              >
                {(
                  [
                    { value: "rtl" as const, label: "עברית RTL" },
                    { value: "auto" as const, label: "Auto" },
                    { value: "ltr" as const, label: "English LTR" },
                  ] as const
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTextDirection(option.value)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      textDirection === option.value
                        ? "bg-[#4B7F73] text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <Textarea
              ref={bodyRef}
              label={undefined}
              placeholder={
                isSingleAudience
                  ? isRtl
                    ? "שלום {first_name},<br/>תזכורת לחשבונית {invoice_number} עם מועד תשלום {due_date}"
                    : "Hello {first_name},<br/>Reminder for invoice {invoice_number} due {due_date}"
                  : isRtl
                    ? "שלום {first_name},<br/>{invoice_list}"
                    : "Hello {first_name},<br/>{invoice_list}"
              }
              value={formData.body}
              onFocus={() => setActiveField("body")}
              onChange={(e) => updateField("body", e.currentTarget.value)}
              minRows={6}
              required
              radius="md"
              dir={isRtl ? "rtl" : "ltr"}
              description={
                isRtl
                  ? "תמיכה ב-HTML. לחצו על משתנה למטה כדי להוסיף."
                  : "HTML is supported. Click a variable below to insert."
              }
              styles={{
                description: isRtl ? { textAlign: "right" as const } : undefined,
                input: {
                  resize: "vertical",
                  ...rtlInputStyles(isRtl),
                },
              }}
            />

            <EmailTemplateVariablePicker
              variables={bodyPickerVariables}
              onInsert={handleInsertVariable}
              disabled={isLoading}
              label={bodyPickerMeta.label}
              description={bodyPickerMeta.description}
            />

            {!isSingleAudience ? (
              <div className="space-y-3 rounded-lg border border-dashed border-gray-200 bg-[#FAFAFA] p-3">
                <div>
                  <label className="text-sm font-medium text-gray-800">
                    Invoice list format
                  </label>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Defines how each invoice appears inside {"{invoice_list}"}.
                    One line per invoice.
                  </p>
                </div>
                <Textarea
                  ref={listFormatRef}
                  value={formData.invoice_list_format}
                  onFocus={() => setActiveField("invoice_list_format")}
                  onChange={(e) =>
                    updateField("invoice_list_format", e.currentTarget.value)
                  }
                  minRows={2}
                  radius="md"
                  dir={isRtl ? "rtl" : "ltr"}
                  placeholder={DEFAULT_INVOICE_LIST_FORMAT}
                  styles={{
                    input: { ...rtlInputStyles(isRtl) },
                  }}
                />
                <EmailTemplateVariablePicker
                  variables={listFormatPickerVariables}
                  onInsert={handleInsertVariable}
                  disabled={isLoading}
                  label="List row variables"
                  description="Per-invoice placeholders for each line inside {invoice_list}."
                />
                <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    List preview
                  </p>
                  <div
                    className="text-sm text-gray-700"
                    dir={isRtl ? "rtl" : "ltr"}
                    dangerouslySetInnerHTML={{ __html: previewListFormat }}
                  />
                </div>
              </div>
            ) : null}
          </FormSection>

          <FormSection
            icon={IconSettings}
            title="Trigger settings"
            description="When this template runs and which invoices qualify."
          >
            <Select
              label="Trigger Type"
              data={triggerTypeOptions}
              value={formData.trigger_type}
              onChange={(value) => {
                const triggerType = (value || "DUE_DATE") as TriggerType;
                onFormDataChange({
                  ...formData,
                  trigger_type: triggerType,
                  repeat_interval_days:
                    triggerType === "OVERDUE_RECURRING"
                      ? formData.repeat_interval_days || 5
                      : 0,
                  invoice_inclusion_scope:
                    triggerType === "OVERDUE_RECURRING"
                      ? formData.invoice_inclusion_scope || "OVERDUE_OPEN"
                      : "OVERDUE_OPEN",
                });
              }}
              required
              radius="md"
              leftSection={<IconClock size={16} />}
              styles={inputLabelStyles}
            />

            <div className="space-y-1">
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <TextInput
                  label={isRecurring ? "Minimum days overdue" : "Offset Days"}
                  type="number"
                  placeholder={
                    isRecurring
                      ? "e.g., 90"
                      : "e.g., -3 for 3 days before, +7 for 7 days after"
                  }
                  value={formData.offset_days}
                  onChange={(e) =>
                    updateField(
                      "offset_days",
                      parseInt(e.currentTarget.value) || 0,
                    )
                  }
                  required
                  radius="md"
                  styles={inputLabelStyles}
                />

                <Select
                  label="Balance Condition"
                  data={conditionBalanceOptions}
                  value={formData.condition_balance}
                  onChange={(value) =>
                    updateField(
                      "condition_balance",
                      (value || "ANY") as ConditionBalance,
                    )
                  }
                  required
                  radius="md"
                  styles={inputLabelStyles}
                />
              </div>
              <p className="text-xs text-gray-500">
                {isRecurring
                  ? "Minimum calendar days overdue before a customer qualifies"
                  : "-5 = 5 days before event, +5 = 5 days after event"}
              </p>
            </div>

            {isRecurring ? (
              <div className="space-y-4 rounded-lg border border-gray-100 bg-[#FAFAFA] p-3">
                <TextInput
                  label="Repeat every (business days)"
                  type="number"
                  placeholder="e.g., 5 — use 0 for once only"
                  value={formData.repeat_interval_days}
                  onChange={(e) =>
                    updateField(
                      "repeat_interval_days",
                      parseInt(e.currentTarget.value) || 0,
                    )
                  }
                  description="Business days between template runs. 0 = run once only."
                  required
                  radius="md"
                  leftSection={<IconClock size={16} />}
                  styles={inputLabelStyles}
                />

                <Select
                  label="Invoice inclusion scope"
                  data={inclusionScopeOptions}
                  value={formData.invoice_inclusion_scope}
                  onChange={(value) =>
                    updateField(
                      "invoice_inclusion_scope",
                      (value || "OVERDUE_OPEN") as InvoiceInclusionScope,
                    )
                  }
                  required
                  radius="md"
                  styles={inputLabelStyles}
                />

                {formData.invoice_inclusion_scope === "OVERDUE_SINCE" ? (
                  <TextInput
                    label="Minimum days overdue for inclusion"
                    type="number"
                    value={formData.inclusion_min_days_overdue}
                    onChange={(e) =>
                      updateField(
                        "inclusion_min_days_overdue",
                        parseInt(e.currentTarget.value) || 0,
                      )
                    }
                    required
                    radius="md"
                    styles={inputLabelStyles}
                  />
                ) : null}
              </div>
            ) : null}
          </FormSection>

          <FormSection
            icon={IconUsers}
            title="Delivery"
            description="Control whether this template sends automatically."
          >
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-[#FAFAFA] px-4 py-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[#4B7F73] ring-1 ring-gray-200">
                  <IconToggleRight size={16} />
                </div>
                <div className="min-w-0">
                  <Text size="sm" fw={500}>
                    {formData.enabled ? "Auto-send on" : "Auto-send off"}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    {formData.enabled
                      ? "Sends automatically when trigger conditions are met"
                      : "Manual and bulk draft generation still use this template; only scheduled auto-send is off"}
                  </Text>
                </div>
              </div>
              <Switch
                checked={formData.enabled}
                onChange={(e) =>
                  updateField("enabled", e.currentTarget.checked)
                }
                size="md"
                color="#4B7F73"
              />
            </div>
          </FormSection>

          <FormSection
            icon={IconEye}
            title="Preview"
            description="See how the email looks with sample data."
          >
            <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-[#FAFAFA] px-3 py-2">
              <Text size="sm" c="dimmed">
                Preview with sample data
              </Text>
              {!isSingleAudience ? (
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={previewMode === "multi"}
                    onChange={(e) =>
                      setPreviewMode(e.target.checked ? "multi" : "single")
                    }
                    className="rounded border-gray-300 text-[#4B7F73] focus:ring-[#4B7F73]"
                  />
                  Preview with 2 invoices
                </label>
              ) : null}
            </div>

            {showMultiWarning ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Per-invoice variables like {"{invoice_number}"} and {"{due_date}"}{" "}
                are empty when several invoices are grouped. Use {"{invoice_list}"}{" "}
                in the body and set per-invoice fields in the list format below.
              </div>
            ) : null}

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="space-y-1.5 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs">
                <div className="flex gap-2">
                  <span className="w-12 shrink-0 font-medium text-gray-500">
                    To
                  </span>
                  <span className="text-gray-700">customer@example.com</span>
                </div>
                <div className="flex gap-2">
                  <span className="w-12 shrink-0 font-medium text-gray-500">
                    From
                  </span>
                  <span className="text-gray-700">you@company.com</span>
                </div>
                <div className="flex gap-2">
                  <span className="w-12 shrink-0 font-medium text-gray-500">
                    Subject
                  </span>
                  <span
                    className="font-medium text-gray-900"
                    dir={isRtl ? "rtl" : "ltr"}
                    style={{ textAlign: isRtl ? "right" : "left" }}
                  >
                    {previewSubject}
                  </span>
                </div>
              </div>
              <div
                dir={isRtl ? "rtl" : "ltr"}
                lang={isRtl ? "he" : undefined}
                className="max-h-[200px] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-gray-700"
                style={{
                  whiteSpace: "pre-wrap",
                  direction: isRtl ? "rtl" : "ltr",
                  textAlign: isRtl ? "right" : "left",
                  unicodeBidi: isRtl ? "plaintext" : undefined,
                }}
                dangerouslySetInnerHTML={{
                  __html: previewBody,
                }}
              />
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5">
                <p className="mb-1.5 text-xs font-medium text-gray-500">
                  Attachments
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700">
                    <IconFileText size={14} className="text-red-500" />
                    invoice-1042.pdf
                  </span>
                  {previewMode === "multi" ? (
                    <span className="text-xs text-gray-500">
                      + other matching invoices
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </FormSection>
        </div>

        <div className="mt-4 shrink-0 border-t border-gray-200 bg-white pt-4">
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={isLoading}
              radius="md"
              color="gray"
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              loading={isLoading}
              radius="md"
              leftSection={<IconMail size={18} />}
              styles={{
                root: {
                  backgroundColor: "#4B7F73",
                  "&:hover": { backgroundColor: "#3F6C61" },
                },
              }}
            >
              {isEditing ? "Update Template" : "Create Template"}
            </Button>
          </Group>
        </div>
      </div>
    </Modal>
  );
}
